<template>
  <div>
    <base-header class="pb-6">
      <b-row align-v="center" class="py-4">
        <b-col lg="6" cols="7">
          <h6 class="h2 text-white d-inline-block mb-0">Company</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </b-col>
      </b-row>
    </base-header>
    <b-container fluid class="mt--6">
      <loading
        :active.sync="loading"
        :can-cancel="false"
        :is-full-page="true"
      ></loading>

      <b-row class="starter-page" v-if="!loading && company">
        <b-col cols="12" md="6">
          <b-card>
            <b-card-header>
              <h1 class="h3">
                <i class="fa fa-building mr-2 text-muted"></i>
                {{ company.name | decode }}
                <small class="text-muted text-uppercase ml-2 mr-2">{{
                  company.number
                }}</small>
                <b-badge
                  v-if="company.status === 'active'"
                  pill
                  variant="success"
                  >Active</b-badge
                >
                <b-badge
                  v-if="company.status === 'dissolved'"
                  pill
                  variant="info"
                  >Dissolved</b-badge
                >
              </h1>
            </b-card-header>

            <b-list-group flush>
              <b-list-group-item>
                <i class="fa fa-fw fa-map-marker mr-2 text-muted"></i>
                {{ company.address | decode }}
              </b-list-group-item>

              <b-list-group-item>
                <i class="fa fa-fw fa-calendar-check mr-2 text-muted"></i>
                Incorporated on {{ company.incorporationDate }} in
                {{ company.jurisdiction | humanize }} ({{
                  company.type | humanize
                }})
              </b-list-group-item>

              <b-list-group-item>
                <i class="fa fa-fw fa-lemon mr-2 text-muted"></i>
                {{ company.businessActivity | decode }}
              </b-list-group-item>

              <b-list-group-item>
                <i class="fa fa-fw fa-clock mr-2 text-muted"></i>
                Next year end:
                {{ company.nextYearEndDate | moment("from", "now") }} ({{
                  company.nextYearEndDate | moment("MMMM Do YYYY")
                }})
              </b-list-group-item>
            </b-list-group>

            <b-card-footer class="text-right">
              <b-btn
                variant="default"
                :to="`/companies/${company.id}/edit`"
                size="sm"
              >
                <i class="fa fa-edit mr-2"></i> Update company details
              </b-btn>
            </b-card-footer>
          </b-card>

          <b-card>
            <b-card-header>
              <h2 class="h3">
                <i class="fa fa-users mr-2 text-muted"></i>
                Share capital and members
              </h2>
            </b-card-header>

            <b-card-body>
              <h3>Share classes</h3>
              <b-list-group>
                <b-list-group-item
                  v-if="company.shareClasses.length < 1"
                  variant="warning"
                  >No share classes yet</b-list-group-item
                >

                <share-class-list-group-item
                  :share-class="shareClass"
                  :company-id="company.id"
                  :currency="company.currency"
                  :account-id="account.id"
                  :share-movements="company.shareMovements"
                  v-for="shareClass in company.shareClasses"
                  :key="shareClass.id"
                  @share-class-deleted="handleShareClassDeleted"
                  @share-class-updated="handleShareClassUpdated"
                ></share-class-list-group-item>

                <b-list-group-item
                  button
                  @click="toggleCreateShareClassForm"
                  v-if="!showCreateShareClassForm"
                  class="text-center text-sm"
                  variant="light"
                  title="Add a share class"
                  v-b-tooltip.hover
                >
                  <i class="fa fa-plus"></i>
                </b-list-group-item>

                <b-list-group-item v-if="showCreateShareClassForm">
                  <share-class-create-form
                    ref="createShareClassForm"
                    :company-id="company.id"
                    :currency="company.currency"
                    @cancel="showCreateShareClassForm = false"
                    @share-class-created="handleShareClassCreated"
                  ></share-class-create-form>
                </b-list-group-item>
              </b-list-group>

              <h3 class="mt-5">Allotments and transfers</h3>
              <div
                class="timeline timeline-one-side"
                data-timeline-content="axis"
                data-timeline-axis-style="dashed"
              >
                <div
                  v-for="movement in company.shareMovements"
                  :key="movement.id"
                  class="timeline-block mb-3"
                >
                  <span
                    class="timeline-step"
                    :class="{
                      'badge-success': movement.type === 'allotment',
                      'badge-info': movement.type === 'transfer',
                    }"
                  >
                    <i
                      v-if="movement.type === 'transfer'"
                      class="fa fa-arrow-right"
                    ></i>
                    <i
                      v-if="movement.type === 'allotment'"
                      class="fa fa-certificate"
                    ></i>
                  </span>
                  <div class="timeline-content">
                    <div class="d-flex justify-content-between pt-1">
                      <div>
                        <span class="text-muted text-sm font-weight-bold">{{
                          movement.type | humanize
                        }}</span>
                      </div>
                      <div class="text-right">
                        <small class="text-muted mr-3">
                          <i class="fas fa-calendar-day mr-1"></i>
                          {{ movement.date | moment("MMM Do YYYY") }}
                        </small>

                        <share-movement-update-buttons
                          :share-movement-id="movement.id"
                          :account-id="account.id"
                          :company-id="company.id"
                          @share-movement-deleted="handleShareMovementDeleted"
                        ></share-movement-update-buttons>
                      </div>
                    </div>
                    <contact-edit-form
                      :account-id="account.id"
                      :modal-id="`edit-contact-${movement.id}-${movement.recipient.id}`"
                      :contact="movement.recipient"
                      @update-contact="movement.recipient = $event"
                    ></contact-edit-form>
                    <contact-edit-form
                      v-if="movement.type === 'transfer'"
                      :account-id="account.id"
                      :modal-id="`edit-contact-${movement.id}-${movement.transferor.id}`"
                      :contact="movement.transferor"
                      @update-contact="movement.transferor = $event"
                    ></contact-edit-form>
                    <h6 class="text-sm mt-1 mb-0">
                      {{ movement.type | humanize }} of
                      {{ movement.numberOfShares }}
                      {{ movement.shareClass.name | decode }}
                      {{ "share" | pluralize(movement.numberOfShares) }}
                      <span v-if="movement.type === 'transfer'"
                        >from
                        <span
                          class="clickable"
                          v-b-modal="
                            `edit-contact-${movement.id}-${movement.transferor.id}`
                          "
                          title="Edit contact"
                          v-b-tooltip.hover
                          >{{ movement.transferor.name | decode }}</span
                        ></span
                      >
                      to
                      <span
                        class="clickable"
                        v-b-modal="
                          `edit-contact-${movement.id}-${movement.recipient.id}`
                        "
                        title="Edit contact"
                        v-b-tooltip.hover
                        >{{ movement.recipient.name | decode }}</span
                      >
                    </h6>
                  </div>
                </div>
              </div>
              <b-row
                v-if="
                  !showCreateShareAllotmentForm && !showCreateShareTransferForm
                "
              >
                <b-col>
                  <b-btn
                    @click="toggleCreateShareAllotmentForm"
                    variant="outline-secondary"
                    block
                    :disabled="company.shareClasses.length < 1"
                  >
                    <i class="fa fa-certificate mr-2"></i> Record allotment
                  </b-btn>
                </b-col>
                <b-col>
                  <b-btn
                    @click="toggleCreateShareTransferForm"
                    variant="outline-secondary  "
                    block
                    :disabled="
                      company.shareClasses.length < 1 ||
                      company.shareMovements.length < 1
                    "
                  >
                    <i class="fa fa-random mr-2"></i> Record transfer
                  </b-btn>
                </b-col>
              </b-row>

              <div v-if="showCreateShareAllotmentForm">
                <share-allotment-create-form
                  ref="createShareAllotmentForm"
                  :accountId="account.id"
                  :companyId="company.id"
                  :currency="company.currency"
                  :share-classes="company.shareClasses"
                  :shareholders="company.shareholders"
                  :company-incorporation-date="company.incorporationDate"
                  @cancel="toggleCreateShareAllotmentForm"
                  @share-movement-created="handleShareAllocationCreated"
                ></share-allotment-create-form>
              </div>
              <div v-if="showCreateShareTransferForm">
                <share-transfer-create-form
                  ref="createShareTransferForm"
                  :accountId="account.id"
                  :companyId="company.id"
                  :currency="company.currency"
                  :share-classes="company.shareClasses"
                  :shareholders="company.shareholders"
                  @cancel="toggleCreateShareTransferForm"
                  @share-movement-created="handleShareTransferCreated"
                ></share-transfer-create-form>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col cols="12" md="6">
          <b-card>
            <b-card-header>
              <h2 class="h3">
                <i class="fa fa-users mr-2 text-muted"></i>
                Officers
              </h2>
            </b-card-header>

            <b-card-body>
              <h3>Directors</h3>
              <b-list-group>
                <b-list-group-item
                  v-if="company.directors.length < 1"
                  variant="warning"
                  >No directors yet</b-list-group-item
                >

                <appointment-list-group-item
                  :appointment="directorship"
                  :companyId="company.id"
                  :accountId="account.id"
                  v-for="directorship in company.directors"
                  :key="directorship.id"
                  @appointment-deleted="handleDirectorDeleted"
                  @appointment-updated="handleDirectorUpdated"
                ></appointment-list-group-item>

                <b-list-group-item
                  button
                  @click="toggleCreateDirectorForm"
                  v-if="!showCreateDirectorForm"
                  class="text-center text-sm"
                  variant="light"
                  title="Add a director"
                  v-b-tooltip.hover
                >
                  <i class="fa fa-user-plus"></i>
                </b-list-group-item>

                <b-list-group-item v-if="showCreateDirectorForm">
                  <appointment-create-form
                    ref="createDirectorForm"
                    :company-id="company.id"
                    type="director"
                    :existing-appointments="company.directors"
                    :company-incorporation-date="company.incorporationDate"
                    @cancel="showCreateDirectorForm = false"
                    @create-appointment="handleDirectorCreated"
                  ></appointment-create-form>
                </b-list-group-item>
              </b-list-group>

              <h3 class="mt-5">Secretaries</h3>
              <b-list-group>
                <b-list-group-item
                  v-if="company.secretaries.length < 1"
                  variant="warning"
                  >No secretaries yet</b-list-group-item
                >

                <appointment-list-group-item
                  :appointment="secretarieship"
                  :companyId="company.id"
                  :accountId="account.id"
                  v-for="secretarieship in company.secretaries"
                  :key="secretarieship.id"
                  @appointment-deleted="handleSecretaryDeleted"
                  @appointment-updated="handleSecretaryUpdated"
                ></appointment-list-group-item>

                <b-list-group-item
                  button
                  @click="toggleCreateSecretaryForm"
                  v-if="!showCreateSecretaryForm"
                  class="text-center text-sm"
                  variant="light"
                  title="Add a secretary"
                  v-b-tooltip.hover
                >
                  <i class="fa fa-user-plus"></i>
                </b-list-group-item>

                <b-list-group-item v-if="showCreateSecretaryForm">
                  <appointment-create-form
                    ref="createSecretaryForm"
                    :company-id="company.id"
                    :existing-appointments="company.secretaries"
                    :company-incorporation-date="company.incorporationDate"
                    type="secretary"
                    @cancel="showCreateSecretaryForm = false"
                    @create-appointment="handleSecretaryCreated"
                  ></appointment-create-form>
                </b-list-group-item>
              </b-list-group>

              <h3 class="mt-5">Beneficial Ownership Officer</h3>
              <b-list-group>
                <b-list-group-item
                  v-if="beneficialOwnershipOfficers.length < 1"
                  variant="warning"
                  >No Beneficial Ownership Officer yet</b-list-group-item
                >

                <appointment-list-group-item
                  :appointment="officership"
                  :companyId="company.id"
                  :accountId="account.id"
                  v-for="officership in beneficialOwnershipOfficers"
                  :key="officership.id"
                  @appointment-deleted="handleBeneficialOwnershipOfficerDeleted"
                  @appointment-updated="handleBeneficialOwnershipOfficerUpdated"
                ></appointment-list-group-item>

                <b-list-group-item
                  button
                  @click="toggleCreateBeneficialOwnershipOfficerForm"
                  v-if="!showCreateBeneficialOwnershipOfficerForm"
                  class="text-center text-sm"
                  variant="light"
                  title="Add or change beneficial ownership officer"
                  v-b-tooltip.hover
                >
                  <i class="fa fa-user-plus"></i>
                </b-list-group-item>

                <b-list-group-item
                  v-if="showCreateBeneficialOwnershipOfficerForm"
                >
                  <appointment-create-form
                    ref="createBeneficialOwnershipOfficerForm"
                    :company-id="company.id"
                    :existing-appointments="beneficialOwnershipOfficers"
                    :company-incorporation-date="company.incorporationDate"
                    type="beneficial_ownership_officer"
                    @cancel="showCreateBeneficialOwnershipOfficerForm = false"
                    @create-appointment="
                      handleBeneficialOwnershipOfficerCreated
                    "
                  ></appointment-create-form>
                </b-list-group-item>
              </b-list-group>
            </b-card-body>
          </b-card>

          <b-card>
            <b-card-header>
              <h2 class="h3">
                <i class="fa fa-copy mr-2 text-muted"></i>
                Documents
              </h2>
            </b-card-header>

            <b-card-body v-if="loadingDocuments">LOADING</b-card-body>
            <b-list-group v-else flush>
              <b-list-group-item v-if="!documents.length"
                >No documents yet</b-list-group-item
              >
              <b-list-group-item
                v-for="document in documents"
                :key="document.id"
                :to="`/companies/${company.id}/documents/${document.id}`"
                class="d-flex justify-content-between align-items-center"
              >
                {{ document.shortType | humanize }}
                <span>
                  <!-- files -->
                  <span v-for="file in document.files" :key="file.id">
                    <b-btn
                      v-if="
                        file.version === document.version &&
                        file.status === 'available'
                      "
                      size="sm"
                      target="_blank"
                      class="mr-2"
                      :href="file.downloadUrl"
                      @click.stop
                      :title="`Download ${file.type} file (${file.id.slice(
                        -4
                      )}-${file.version.slice(-4)})`"
                      v-b-tooltip.hover
                    >
                      <i
                        v-if="file.type === 'docx'"
                        class="fa fa-file-word"
                      ></i>
                      <i v-if="file.type === 'pdf'" class="fa fa-file-pdf"></i>
                    </b-btn>
                  </span>
                </span>

                <b-badge v-if="document.isFiled" variant="success" pill>
                  Filed
                  {{ document.filingDate | moment("MMM DD YYYY") }}
                </b-badge>
                <b-badge v-else-if="!document.isFiled" variant="warning" pill>
                  Not filed (due
                  {{ document.filingDueDate | moment("from", "now") }})
                </b-badge>
              </b-list-group-item>
            </b-list-group>

            <b-card-footer class="text-right">
              <b-btn
                variant="default"
                :to="`/companies/${company.id}/documents/create`"
                size="sm"
              >
                <i class="fa fa-file-medical mr-2"></i> Create new document
              </b-btn>
            </b-card-footer>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>
<script>
import _ from "underscore";
import { get } from "dot-prop";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import AppointmentCreateForm from "../../Appointments/AppointmentCreateForm";
import AppointmentListGroupItem from "../../Appointments/AppointmentListGroupItem";
import ShareClassCreateForm from "./ShareClassCreateForm";
import ShareClassListGroupItem from "./ShareClassListGroupItem";
import ShareAllotmentCreateForm from "./ShareAllotmentCreateForm";
import ShareTransferCreateForm from "./ShareTransferCreateForm";
import ShareMovementUpdateButtons from "./ShareMovementUpdateButtons";
import ContactEditForm from "../../Contacts/ContactEditForm";

export default {
  name: "company-view",
  components: {
    RouteBreadCrumb,
    Loading,
    AppointmentCreateForm,
    AppointmentListGroupItem,
    ShareClassCreateForm,
    ShareClassListGroupItem,
    ShareAllotmentCreateForm,
    ShareTransferCreateForm,
    ShareMovementUpdateButtons,
    ContactEditForm,
  },
  props: {
    companyId: {
      type: String,
      description: "Company ID",
      required: true,
    },
  },
  data() {
    return {
      company: null,
      loading: false,
      loadingDocuments: false,
      documents: [],
      errorMessage: null,
      documentsErrorMessage: null,
      showCreateDirectorForm: false,
      showCreateSecretaryForm: false,
      showCreateBeneficialOwnershipOfficerForm: false,
      showCreateShareClassForm: false,
      showCreateShareAllotmentForm: false,
      showCreateShareTransferForm: false,
    };
  },
  computed: {
    account: function () {
      return this.$store.state.account;
    },
    beneficialOwnershipOfficers: function () {
      if (this.company.beneficialOwnershipOfficer) {
        return [this.company.beneficialOwnershipOfficer];
      } else {
        return [];
      }
    },
  },
  mounted() {
    this.loadCompany();
    this.loadDocuments();
  },
  methods: {
    toggleCreateDirectorForm() {
      this.showCreateDirectorForm = !this.showCreateDirectorForm;

      if (this.showCreateDirectorForm === true) {
        this.$nextTick(() => {
          this.$refs.createDirectorForm.setFocus();
        });
      }
    },
    toggleCreateSecretaryForm() {
      this.showCreateSecretaryForm = !this.showCreateSecretaryForm;

      if (this.showCreateSecretaryForm === true) {
        this.$nextTick(() => {
          this.$refs.createSecretaryForm.setFocus();
        });
      }
    },
    toggleCreateBeneficialOwnershipOfficerForm() {
      this.showCreateBeneficialOwnershipOfficerForm = !this
        .showCreateBeneficialOwnershipOfficerForm;

      if (this.showCreateBeneficialOwnershipOfficerForm === true) {
        this.$nextTick(() => {
          this.$refs.createBeneficialOwnershipOfficerForm.setFocus();
        });
      }
    },
    toggleCreateShareClassForm() {
      this.showCreateShareClassForm = !this.showCreateShareClassForm;

      if (this.showCreateShareClassForm === true) {
        this.$nextTick(() => {
          this.$refs.createShareClassForm.setFocus();
        });
      }
    },
    toggleCreateShareAllotmentForm() {
      this.showCreateShareAllotmentForm = !this.showCreateShareAllotmentForm;

      if (this.showCreateShareAllotmentForm === true) {
        this.showCreateShareTransferForm = false;
        this.$nextTick(() => {
          this.$refs.createShareAllotmentForm.setFocus();
        });
      }
    },
    toggleCreateShareTransferForm() {
      this.showCreateShareTransferForm = !this.showCreateShareTransferForm;

      if (this.showCreateShareTransferForm === true) {
        this.showCreateShareAllotmentForm = false;
        this.$nextTick(() => {
          this.$refs.createShareTransferForm.setFocus();
        });
      }
    },
    handleDirectorCreated(appointment) {
      // update UI - appointment does not include contact
      const newAppointment = appointment;
      newAppointment.contact = { name: "Loading..." };
      this.company.directors.push(newAppointment);

      // load from API
      this.loadCompany(false);
      this.showCreateDirectorForm = false;
    },
    handleSecretaryCreated(appointment) {
      // update UI - appointment does not include contact
      const newAppointment = appointment;
      newAppointment.contact = { name: "Loading..." };
      this.company.secretaries.push(newAppointment);

      this.loadCompany(false);
      this.showCreateSecretaryForm = false;
    },
    handleBeneficialOwnershipOfficerCreated(appointment) {
      // update UI - appointment does not include contact
      const newAppointment = appointment;
      newAppointment.contact = { name: "Loading..." };
      this.company.beneficialOwnershipOfficer = newAppointment;

      this.loadCompany(false);
      this.showCreateBeneficialOwnershipOfficerForm = false;
    },
    handleShareClassCreated(shareClass) {
      // update UI

      this.company.shareClasses.push(shareClass);
      this.loadCompany(false);
      this.showCreateShareClassForm = false;
    },
    handleDirectorDeleted(appointment) {
      _.each(this.company.directors, (director, index) => {
        if (director.id === appointment.id) {
          this.company.directors.splice(index, 1);
        }
      });

      this.loadCompany(false);
    },
    handleDirectorUpdated(appointment) {
      _.each(this.company.directors, (director, index) => {
        if (director.id === appointment.id) {
          this.company.directors.splice(index, 1, appointment);
        }
      });

      this.loadCompany(false);
    },
    handleSecretaryDeleted(appointment) {
      _.each(this.company.secretaries, (secretary, index) => {
        if (secretary.id === appointment.id) {
          this.company.secretaries.splice(index, 1);
        }
      });

      this.loadCompany(false);
    },
    handleSecretaryUpdated(appointment) {
      _.each(this.company.secretaries, (secretary, index) => {
        if (secretary.id === appointment.id) {
          this.company.secretaries.splice(index, 1, appointment);
        }
      });

      this.loadCompany(false);
    },
    handleBeneficialOwnershipOfficerDeleted(appointment) {
      this.company.beneficialOwnershipOfficer = null;
      this.loadCompany(false);
    },
    handleBeneficialOwnershipOfficerUpdated(appointment) {
      this.company.beneficialOwnershipOfficer = this.appointment;
      this.loadCompany(false);
    },
    handleShareClassDeleted(shareClass) {
      _.each(this.company.shareClasses, (item, index) => {
        if (shareClass.id === item.id) {
          this.company.shareClasses.splice(index, 1);
        }
      });

      this.loadCompany(false);
    },
    handleShareClassUpdated(shareClass) {
      _.each(this.company.shareClasses, (item, index) => {
        if (shareClass.id === item.id) {
          this.company.shareClasses.splice(index, 1, appointment);
        }
      });

      this.loadCompany(false);
    },
    handleShareAllocationCreated(shareMovement) {
      // TODO: display in UI less lazily?
      this.loadCompany(false);
      this.showCreateShareAllotmentForm = false;
    },
    handleShareTransferCreated() {
      // TODO: display in UI less lazily?
      this.loadCompany(false);
      this.showCreateShareTransferForm = false;
    },
    handleShareMovementDeleted(shareMovementId) {
      const index = _.findIndex(this.company.shareMovements, (movement) => {
        return movement.id === shareMovementId;
      });

      // remove from UI
      this.company.shareMovements.splice(index, 1);

      // reload company
      this.loadCompany(false);
    },
    loadCompany(setLoadingState = true) {
      if (setLoadingState) {
        this.loading = true;
      }
      this.axios
        .get(`/v1/accounts/${this.account.id}/companies/${this.companyId}`)
        .then((response) => {
          if (setLoadingState) {
            this.loading = false;
          }
          this.company = response.data;
        })
        .catch((error) => {
          if (setLoadingState) {
            this.loading = false;
          }
          this.errorMessage = "Failed to load comapny";
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        });
    },
    loadDocuments() {
      this.loadingDocuments = true;
      this.axios
        .get(
          `/v1/accounts/${this.account.id}/companies/${this.companyId}/documents?expand=files`
        )
        .then((response) => {
          this.loadingDocuments = false;
          this.documents = response.data;
        })
        .catch((error) => {
          this.loadingDocuments = false;
          this.documentsErrorMessage = "Failed to load documents";
          if (get(error, "response.data.message")) {
            this.documentsErrorMessage = error.response.data.message;
          }
        });
    },
  },
};
</script>
<style>
.starter-page {
  min-height: calc(100vh - 380px);
}
</style>
